(function (root, factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (typeof document !== 'undefined') {
    root.BloomingfulDeliveryEstimate = api;

    var start = function () {
      api.render(document);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }

    document.addEventListener('shopify:section:load', function (event) {
      api.render(event.target || document);
    });
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var holidayCache = {};

  function normalizedDate(value) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12);
  }

  function dateKey(value) {
    return [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, '0'),
      String(value.getDate()).padStart(2, '0')
    ].join('-');
  }

  function fixedDate(year, month, day) {
    return new Date(year, month, day, 12);
  }

  function nthWeekday(year, month, weekday, occurrence) {
    var date = fixedDate(year, month, 1);
    var offset = (weekday - date.getDay() + 7) % 7;
    date.setDate(1 + offset + (occurrence - 1) * 7);
    return date;
  }

  function lastWeekday(year, month, weekday) {
    var date = fixedDate(year, month + 1, 0);
    var offset = (date.getDay() - weekday + 7) % 7;
    date.setDate(date.getDate() - offset);
    return date;
  }

  function addObservedHoliday(holidays, year, month, day) {
    var observed = fixedDate(year, month, day);
    if (observed.getDay() === 6) observed.setDate(observed.getDate() - 1);
    if (observed.getDay() === 0) observed.setDate(observed.getDate() + 1);
    holidays.add(dateKey(observed));
  }

  function holidaysForYear(year) {
    if (holidayCache[year]) return holidayCache[year];

    var holidays = new Set();
    addObservedHoliday(holidays, year, 0, 1);
    addObservedHoliday(holidays, year, 5, 19);
    addObservedHoliday(holidays, year, 6, 4);
    addObservedHoliday(holidays, year, 10, 11);
    addObservedHoliday(holidays, year, 11, 25);
    holidays.add(dateKey(nthWeekday(year, 0, 1, 3)));
    holidays.add(dateKey(nthWeekday(year, 1, 1, 3)));
    holidays.add(dateKey(lastWeekday(year, 4, 1)));
    holidays.add(dateKey(nthWeekday(year, 8, 1, 1)));
    holidays.add(dateKey(nthWeekday(year, 9, 1, 2)));
    holidays.add(dateKey(nthWeekday(year, 10, 4, 4)));
    holidayCache[year] = holidays;
    return holidays;
  }

  function isObservedFederalHoliday(value) {
    var key = dateKey(value);
    var year = value.getFullYear();
    return holidaysForYear(year - 1).has(key) || holidaysForYear(year).has(key) || holidaysForYear(year + 1).has(key);
  }

  function isBusinessDay(value) {
    var weekday = value.getDay();
    return weekday !== 0 && weekday !== 6 && !isObservedFederalHoliday(value);
  }

  function addBusinessDays(value, count) {
    var date = normalizedDate(value);
    var added = 0;

    while (added < count) {
      date.setDate(date.getDate() + 1);
      if (isBusinessDay(date)) added += 1;
    }

    return date;
  }

  function calculateRange(value) {
    var base = normalizedDate(value);
    return {
      start: addBusinessDays(base, 3),
      end: addBusinessDays(base, 9)
    };
  }

  function pacificDateTime(value) {
    var parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23'
    }).formatToParts(value).reduce(function (result, part) {
      if (part.type !== 'literal') result[part.type] = Number(part.value);
      return result;
    }, {});

    return {
      date: new Date(parts.year, parts.month - 1, parts.day, 12),
      minutes: parts.hour * 60 + parts.minute
    };
  }

  function calculateEarliestDelivery(value) {
    var pacific = pacificDateTime(value);
    var shipDate = normalizedDate(pacific.date);

    if (pacific.minutes >= 11 * 60 || !isBusinessDay(shipDate)) {
      shipDate = addBusinessDays(shipDate, 1);
    }

    return addBusinessDays(shipDate, 1);
  }

  function monthName(value) {
    return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(value);
  }

  function formatRange(range) {
    var start = range.start;
    var end = range.end;
    var sameYear = start.getFullYear() === end.getFullYear();
    var sameMonth = sameYear && start.getMonth() === end.getMonth();

    if (sameMonth) {
      return monthName(start) + ' ' + start.getDate() + ' to ' + end.getDate();
    }

    if (sameYear) {
      return monthName(start) + ' ' + start.getDate() + ' to ' + monthName(end) + ' ' + end.getDate();
    }

    return monthName(start) + ' ' + start.getDate() + ', ' + start.getFullYear() +
      ' to ' + monthName(end) + ' ' + end.getDate() + ', ' + end.getFullYear();
  }

  function deliveryText(value) {
    return 'Standard Delivery ' + formatRange(calculateRange(value));
  }

  function formatDeliveryDate(value) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: value.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    }).format(value);
  }

  function earliestDeliveryText(value) {
    return 'Earliest Delivery ' + formatDeliveryDate(calculateEarliestDelivery(value));
  }

  function render(scope) {
    var now = new Date();
    var text = deliveryText(now);
    var earliestText = earliestDeliveryText(now);
    scope.querySelectorAll('[data-bf-standard-delivery-estimate]').forEach(function (element) {
      element.textContent = text;
    });
    scope.querySelectorAll('[data-bf-earliest-delivery]').forEach(function (element) {
      element.textContent = earliestText;
    });
  }

  return {
    addBusinessDays: addBusinessDays,
    calculateEarliestDelivery: calculateEarliestDelivery,
    calculateRange: calculateRange,
    deliveryText: deliveryText,
    earliestDeliveryText: earliestDeliveryText,
    formatDeliveryDate: formatDeliveryDate,
    formatRange: formatRange,
    isBusinessDay: isBusinessDay,
    isObservedFederalHoliday: isObservedFederalHoliday,
    render: render
  };
});
